import { ReactElement, useMemo, useState } from 'react'
import { IMooringType, IPositionWithDrawing } from '../generated-types'
import React from 'react'
import { ShowDialogProps } from '../show-dialog'
import { useTranslation } from 'react-i18next'
import { ReactComponent as OlexFileIcon } from '../assets/svg/OlextFileIcon.svg'
import { Box, styled } from '@mui/material'
import { useUploadProjectOlex } from 'src/api/project'
import { toast } from 'react-toastify'
import { AppForm } from './FormCore'
import { CommonFileUploader } from './common/input/CommonFileUploader'
import { CancelButton } from './CancelButton'
import { z } from 'zod'
import { useZodForm } from 'src/hooks/zod-form'
import { DialogSpinner } from './Spinner'
import { useUploadMooringOlex } from 'src/api/moorings'

export const CircularProgressBox = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  opacity: 0.8;
  background-color: ${props => props.theme.palette.white[props.theme.palette.mode]};
  z-index: 500;
  display: flex;
  justify-content: center;
  align-items: center;
`

interface OlexFileUploaderProps extends ShowDialogProps {
  UploadButton?: ReactElement
  customerId: EntityId
  localityId: EntityId
  mooringId?: EntityId
  projectId?: EntityId
  handleConfirm: (updatedPositions: IPositionWithDrawing[], uploaderPop?: () => any) => void
  isDialog?: boolean
  projectType?: IMooringType
  noOfCages?: number
  noOfRows?: number
  noOfPens?: number
  serviceRow?: boolean
  bridleNames?: string[]
  selectedProjectTypeIndex?: number
  hasProjectPositions?: boolean
  resetKey?: number
}
export const OlexFileUploader: React.FC<OlexFileUploaderProps> = ({
  pop,
  customerId,
  localityId,
  mooringId,
  projectId,
  handleConfirm,
  isDialog = true,
  noOfCages,
  noOfRows,
  projectType,
  noOfPens,
  serviceRow,
  bridleNames = [],
  selectedProjectTypeIndex,
  resetKey,
}) => {
  const [requiredInputs, setRequiredInputs] = useState(['project-type'])
  const [errorResetKey, setErrorResetKey] = useState(0)
  const { mutateAsync: uploadProjectOlex, isLoading: isUploadingOlex } = useUploadProjectOlex(customerId, localityId)
  const { mutateAsync: uploadMooringOlex, isLoading: isUploadingMooringOlex } = useUploadMooringOlex(
    customerId,
    localityId,
  )
  const { t } = useTranslation(['file-upload'])

  const submitProjectOlexFile = async (formData: FormData) => {
    uploadProjectOlex(formData, {
      onSuccess: data => {
        handleConfirm(data.data, pop)
      },
      onError: err => {
        if (err['data']) {
          toast.error(t(err['data'], { ns: 'position' }))
        } else {
          toast.error(t('unExpectedError', { ns: 'common-api-message' }))
        }
      },
    })
  }

  const submitMooringOlexFile = async (formData: FormData) => {
    uploadMooringOlex(formData, {
      onSuccess: data => {
        handleConfirm(data.data, pop)
      },
      onError: err => {
        toast.error(t(err['data'], { ns: 'position' }))
        setErrorResetKey(prev => prev + 1)
      },
    })
  }

  const prepareBridleFormData = (formData: FormData, bridleNames: string[]) => {
    formData.append('bridleNames', JSON.stringify(bridleNames))
  }

  const prepareLadderCageFormData = (formData: FormData, noOfCages: number, noOfPens: number) => {
    formData.append('noOfCages', noOfCages.toString())
    formData.append('noOfPens', noOfPens.toString())
    formData.append('noOfRows', '1')
  }

  const prepareStandardCageFormData = (formData: FormData, noOfCages: number, noOfRows: number, noOfPens?: number) => {
    formData.append('noOfCages', noOfCages.toString())
    formData.append('noOfRows', noOfRows.toString())
    if (noOfPens !== undefined && noOfPens !== null) {
      formData.append('noOfPens', noOfPens.toString())
    }
  }

  const disabled = useMemo(() => {
    if (projectId !== undefined && projectType !== undefined && localityId !== undefined) {
      if (projectType === IMooringType.LadderCage) {
        setRequiredInputs(['project-type', 'noOfPens', 'noOfCages'])
        return !noOfCages || noOfCages <= 0 || !noOfPens || noOfPens <= 0
      } else if (projectType === IMooringType.Barge) {
        setRequiredInputs(['project-type'])
        return false
      }
      if (projectType === IMooringType.FrameCage) {
        setRequiredInputs(['project-type', 'noOfRows', 'noOfCages', 'noOfPens'])
        return !noOfCages || noOfCages <= 0 || !noOfRows || noOfRows <= 0 || !noOfPens || noOfPens <= 0
      }
      setRequiredInputs(['project-type', 'noOfRows', 'noOfCages'])
      return !noOfCages || noOfCages <= 0 || !noOfRows || noOfRows <= 0
    }
  }, [projectId, noOfCages, noOfRows, projectType, noOfPens, localityId])

  const onUploadOlex = async fileObj => {
    const formData = new FormData()

    formData.append('file', fileObj)

    if (projectType === undefined) return
    if (
      (projectType === IMooringType.FrameCage || projectType === IMooringType.SteelCage) &&
      noOfCages &&
      noOfRows &&
      noOfCages % noOfRows !== 0
    ) {
      toast.error(t('cages-must-be-multiple-of-rows', { ns: 'mooring' }))
      return
    }

    formData.append('type', projectType.toString())

    switch (projectType) {
      case IMooringType.Barge:
        prepareBridleFormData(formData, bridleNames || [])
        break

      case IMooringType.LadderCage:
        if (!noOfCages || !noOfPens) return
        prepareLadderCageFormData(formData, noOfCages, noOfPens)
        break

      default:
        if (!noOfCages || !noOfRows) return
        prepareStandardCageFormData(formData, noOfCages, noOfRows, noOfPens)
        if (projectId && serviceRow) formData.append('serviceRow', 'true')
        break
    }

    if (mooringId) {
      formData.append('mooringId', mooringId?.toString())
      await submitMooringOlexFile(formData)
    } else if (projectId) {
      formData.append('projectId', projectId?.toString())
      await submitProjectOlexFile(formData)
    } else {
      toast.error(t('error-upload-olex', { ns: 'file-upload' }))
    }
  }

  const fileSchema = z.object({
    fileIds: z.union([z.array(z.string()).min(1), z.array(z.number()).min(1)]),
  })

  const form = useZodForm(fileSchema, {})

  return (
    <>
      <AppForm form={form}>
        <CommonFileUploader
          key={`${resetKey}-${errorResetKey}`}
          name="fileIds"
          acceptedFileTypes={['application/gzip', 'application/x-gzip']}
          onChange={() => {}}
          FileIcon={() => <OlexFileIcon width={'50px'} height={'50px'} />}
          onUploadFile={onUploadOlex}
          isFileServerUpload={false}
          height={isDialog ? '315px' : '150px'}
          isDeletable={false}
          disabled={selectedProjectTypeIndex === -1 ? true : disabled}
          userMessage={
            disabled
              ? t('olex-uploader-text', {
                  inputs: requiredInputs.map(i => t(i, { ns: ['projects', 'mooring'] })).join(', '),
                  ns: 'projects',
                })
              : undefined
          }
        />
        {isDialog && (
          <Box className="flex justify-end gap-4 pt-4">
            <CancelButton onClick={() => pop(true)} />
          </Box>
        )}
      </AppForm>

      {(isUploadingMooringOlex || isUploadingOlex) && <DialogSpinner />}
    </>
  )
}
